type CameraScanner = {
  isScanning: boolean;
  stop: () => Promise<void>;
  clear: () => void;
};

const cameraConfig = {
  fps: 10,
  qrbox: { width: 240, height: 240 },
  aspectRatio: 1,
};

export async function startQrCamera(elementId: string, onDecoded: (text: string) => void | Promise<void>): Promise<CameraScanner> {
  if (!window.isSecureContext) throw new Error("INSECURE_CONTEXT");
  if (!navigator.mediaDevices?.getUserMedia) throw new Error("CAMERA_UNSUPPORTED");

  const { Html5Qrcode } = await import("html5-qrcode");
  const cameras = await Html5Qrcode.getCameras();
  if (!cameras.length) throw new Error("CAMERA_NOT_FOUND");

  const rearCamera = [...cameras].reverse().find((camera) => /back|rear|environment|背面/i.test(camera.label));
  const camera = rearCamera ?? cameras.at(-1)!;
  const scanner = new Html5Qrcode(elementId, { verbose: false });
  let handled = false;

  try {
    await scanner.start(camera.id, cameraConfig, async (decodedText) => {
      if (handled) return;
      handled = true;
      try {
        if (scanner.isScanning) await scanner.stop();
      } catch {
        // The camera can already be closing when a QR is found.
      } finally {
        scanner.clear();
      }
      await onDecoded(decodedText);
    }, () => undefined);
    return scanner;
  } catch (error) {
    scanner.clear();
    throw error;
  }
}

export async function stopQrCamera(scanner: CameraScanner | null): Promise<void> {
  if (!scanner) return;
  try {
    if (scanner.isScanning) await scanner.stop();
  } catch {
    // Treat an already-stopped camera as successfully closed.
  } finally {
    scanner.clear();
  }
}

export async function scanQrImage(elementId: string, file: File): Promise<string> {
  const { Html5Qrcode } = await import("html5-qrcode");
  const scanner = new Html5Qrcode(elementId, { verbose: false });
  try {
    return await scanner.scanFile(file, false);
  } finally {
    scanner.clear();
  }
}

export function cameraErrorMessage(error: unknown): string {
  const message = error instanceof Error ? `${error.name} ${error.message}` : String(error);
  if (message.includes("INSECURE_CONTEXT")) return "カメラはHTTPS接続でのみ使用できます。";
  if (message.includes("CAMERA_UNSUPPORTED")) return "このブラウザではカメラを直接開けません。SafariまたはChromeで開くか、下の「QR画像を読み取る」を使用してください。";
  if (/NotAllowed|Permission|permission|denied|許可/i.test(message)) return "カメラの使用が許可されていません。ブラウザのサイト設定でカメラを許可して、もう一度お試しください。";
  if (/NotFound|DevicesNotFound|CAMERA_NOT_FOUND|Requested device not found/i.test(message)) return "使用できるカメラが見つかりません。下の「QR画像を読み取る」も利用できます。";
  if (/NotReadable|TrackStart|Could not start video/i.test(message)) return "カメラを起動できませんでした。他のアプリでカメラを閉じてから、もう一度お試しください。";
  return "カメラを起動できませんでした。カメラの許可を確認するか、QR画像から読み取ってください。";
}

export function imageScanErrorMessage(): string {
  return "画像からQRコードを読み取れませんでした。QR全体が鮮明に写った画像を選んでください。";
}
